// PageTabs (009 revision 6, FR-046): tabs that are routes (the Users pages, a source's details): a navigation of
// links with the look of the shared `Tabs` (`line` or `pill`), the current one marked `aria-current="page"`. The row
// scrolls sideways inside itself when the tabs do not fit (`data-ui-scroll="x"`). Tabs that switch content without
// changing the address use `Tabs` from `@arcel/ui/ui`.
import { cn } from '../lib/cn';
import { tabsListVariants, tabsTriggerVariants } from '../components/ui/tabs';
import { isCurrent, PlainLink, type LinkLike, type ShellNavItem } from './nav';

/** Props of {@link PageTabs}. */
export interface PageTabsProps {
  /** The navigation landmark's name. */
  label: string;
  items: ShellNavItem[];
  /** The current path, which marks the current tab. */
  currentPath?: string;
  /** The link component (the router's `Link`); a plain anchor by default. */
  link?: LinkLike;
  variant?: 'line' | 'pill';
  className?: string;
}

/**
 * The route tabs.
 * @param props label, items, current path, link component and variant
 * @returns the navigation
 */
export function PageTabs({
  label,
  items,
  currentPath,
  link: Link = PlainLink,
  variant = 'line',
  className,
}: PageTabsProps) {
  return (
    <nav
      aria-label={label}
      data-ui-scroll="x"
      className={cn(tabsListVariants({ variant }), className)}
    >
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          aria-current={isCurrent(item, currentPath) ? 'page' : undefined}
          className={tabsTriggerVariants({ variant })}
        >
          {item.icon}
          <span data-design={item.design}>{item.label}</span>
          {item.badge && <span className="text-xs text-ink-secondary">{item.badge}</span>}
        </Link>
      ))}
    </nav>
  );
}
